import { useState } from 'react'

import { roundSig, ru, significantDigits } from '../../lib/physics'
import { Choice, Predict } from './common'

/**
 * Значащие цифры (урок 7): точность записи — это число значащих цифр, а не знаков после
 * запятой. Одно и то же значение в мм, см, дм и м пишется с разным числом знаков после
 * запятой, но с теми же четырьмя значащими цифрами. Ответ калькулятора округляют до точности данных.
 */

const ЗАПИСИ = ['20,59 мм', '2,059 см', '0,2059 дм', '0,02059 м', '2 см', '2,0 см', '2,00 см'] as const

/** Разметка записи: нули слева — серые, последняя значащая цифра — сомнительная. */
function разметка(запись: string) {
  const [число, ед] = запись.split(' ')
  const всего = significantDigits(число)
  let встречено = 0
  let началось = false
  return (
    <>
      {число.split('').map((с, i) => {
        if (!/\d/.test(с)) return <span key={i}>{с}</span>
        if (!началось && с === '0') return <span key={i} className="muted">{с}</span>
        началось = true
        встречено++
        const сомн = встречено === всего
        return <b key={i} style={{ color: сомн ? 'var(--heavy)' : 'var(--accent)' }}>{с}</b>
      })}{' '}
      {ед}
    </>
  )
}

export default function DigitsLab() {
  const [номер, setНомер] = useState(0)
  const [цифр, setЦифр] = useState<number>(2)

  const запись = ЗАПИСИ[номер]
  const n = significantDigits(запись.split(' ')[0])
  const калькулятор = 1.1 * 1.1 * 9.6 // объём куска мела из урока 6, см³

  return (
    <div className="card">
      <div className="lab-big">{разметка(запись)}</div>
      <div className="lab-readout">
        <span>значащих цифр: <b>{n}</b></span>
        <span className="el">верные цифры</span>
        <span className="heavy">сомнительная — последняя</span>
        <span className="muted">нули слева — не значащие</span>
      </div>
      <div className="lab-sub">Запись</div>
      <Choice options={ЗАПИСИ.map((z, i) => ({ value: i, label: z }))} value={номер} onChange={setНомер} />

      <div className="lab-sub" style={{ marginTop: 18 }}>Ответ калькулятора: кусок мела 1,1 см × 1,1 см × 9,6 см</div>
      <div className="lab-caption">
        V = {ru(калькулятор, 3)} см³ → до {цифр} значащих: <b>{ru(roundSig(калькулятор, цифр), Math.max(0, цифр - 2))} см³</b>
      </div>
      <Choice options={[1, 2, 3, 4].map((k) => ({ value: k, label: `${k} цифр${k === 1 ? 'а' : k < 5 ? 'ы' : ''}` }))} value={цифр} onChange={setЦифр} />

      <Predict
        question="Сколько значащих цифр в записи 0,02059 м?"
        options={['2', '4', '6']}
        right={1}
        revealed={ЗАПИСИ[номер] === '0,02059 м'}
        explain="Нули слева только задают масштаб — это те же 20,59 мм. Значащих цифр четыре: 2, 0, 5, 9."
      />
      <p className="muted small">
        Размеры мела измерены с двумя значащими цифрами — значит, и ответ не точнее: около 12 см³, а не 11,616.
        Нули справа после запятой значащие: «2,00 см» точнее, чем «2 см».
      </p>
    </div>
  )
}
